/*
本文に直に書いた自己完結の SVG の図 (`<div data-lesson-diagram>` と `<div data-zukai-figure>` の塊) だけを描く。
MarkdownContent の allowRawHtml="svg-figures" で使う。

ほかの生の HTML は要素にしない (react-markdown が文字のまま出す)。本文が説明のために書いた `<h1>` が消えないようにするため。
図の塊は、許す要素と属性の表 (下の ALLOWED_*) だけを残して作り直す。表に無いものは中身ごと落とす。

- 落とす要素  script、foreignObject、image、use、a、animate 系、iframe など、表に無いもの全部
- 落とす属性  on* と href を含む表に無いもの全部。生成元の接頭辞 (lesson-diagram-、ld-) を持たない id。
              値に知らない関数 (image-set など)、url(#…) 以外の url()、字の逃がし (\\)、position: fixed があるもの
- <style>     規則ごとに見て、図の塊の印 (lesson-diagram- の id か zukai- の版つきの class) で始まり、入れ子が無く、
              値が安全な規則だけを残す (@media の中も同じ)。ほかのアットルールの規則は落とす。
              @import、@font-face、字の逃がし、`<` があれば <style> ごと落とす
- 包み        描いた図は contain: paint の箱に入れる。図の CSS が配置を変えても、箱の外には描けない
*/
import type { Element, ElementContent, Root, RootContent } from "hast";
import rehypeRaw from "rehype-raw";
import { repairPathData } from "./rehypeSanitizeLessonHtml";

type RawNode = { type: "raw"; value: string };
type Node = RootContent | ElementContent | RawNode;

const FIGURE_START = /^<div\b[^>]*\sdata-(?:lesson-diagram|zukai-figure)=/i;

const ALLOWED_ELEMENTS = new Set([
  "div",
  "style",
  "svg",
  "g",
  "defs",
  "title",
  "desc",
  "text",
  "tspan",
  "rect",
  "path",
  "line",
  "polyline",
  "polygon",
  "circle",
  "ellipse",
  "marker",
  "lineargradient",
  "radialgradient",
  "stop",
  "clippath",
  "mask",
  "pattern",
  "symbol",
  "filter",
  "feblend",
  "fecolormatrix",
  "fecomposite",
  "fedropshadow",
  "feflood",
  "fegaussianblur",
  "femerge",
  "femergenode",
  "femorphology",
  "feoffset",
]);

// hast の属性名を小文字にして - と : を除いた形で持つ (strokeWidth も stroke-width も strokewidth)
const ALLOWED_PROPERTIES = new Set([
  "id",
  "classname",
  "viewbox",
  "xmlns",
  "role",
  "arialabel",
  "ariahidden",
  "arialabelledby",
  "ariadescribedby",
  "x",
  "y",
  "x1",
  "x2",
  "y1",
  "y2",
  "dx",
  "dy",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "fx",
  "fy",
  "width",
  "height",
  "d",
  "points",
  "transform",
  "fill",
  "fillopacity",
  "fillrule",
  "stroke",
  "strokewidth",
  "strokeopacity",
  "strokedasharray",
  "strokedashoffset",
  "strokelinecap",
  "strokelinejoin",
  "strokemiterlimit",
  "opacity",
  "fontsize",
  "fontweight",
  "fontfamily",
  "fontstyle",
  "textanchor",
  "dominantbaseline",
  "alignmentbaseline",
  "letterspacing",
  "textdecoration",
  "textlength",
  "lengthadjust",
  "rotate",
  "refx",
  "refy",
  "markerwidth",
  "markerheight",
  "markerunits",
  "orient",
  "markerstart",
  "markermid",
  "markerend",
  "offset",
  "stopcolor",
  "stopopacity",
  "gradientunits",
  "gradienttransform",
  "spreadmethod",
  "clippath",
  "clippathunits",
  "cliprule",
  "mask",
  "maskunits",
  "patternunits",
  "patterncontentunits",
  "patterntransform",
  "preserveaspectratio",
  "filter",
  "filterunits",
  "stddeviation",
  "in",
  "in2",
  "result",
  "floodcolor",
  "floodopacity",
  "values",
  "type",
  "mode",
  "operator",
  "radius",
  "style",
  "visibility",
  "display",
  "overflow",
  "vectoreffect",
  "paintorder",
  "pathlength",
  "xmlspace",
  "version",
  "focusable",
]);

// CSS と見た目の属性の値で使ってよい関数。ほかの関数 (image-set、cross-fade、src、element など) は外へ取りに行けるので落とす。
// url() は同じ図の中の印 (url(#id)) だけ
const ALLOWED_FUNCTIONS = new Set([
  "var",
  "calc",
  "rgb",
  "rgba",
  "hsl",
  "hsla",
  "min",
  "max",
  "clamp",
  "url",
  "rotate",
  "translate",
  "translatex",
  "translatey",
  "scale",
  "matrix",
  "skewx",
  "skewy",
]);

/** CSS の値 (宣言の並び、見た目の属性の値) として安全か。外への参照、知らない関数、字の逃がし、画面に貼り付く配置を許さない */
function isSafeCssValue(value: string): boolean {
  // CSS のコメント (position:/**/fixed) で検査をすり抜けないよう、先に剥がしてから読む。閉じないコメントは落とす
  if (/\/\*(?![\s\S]*\*\/)/.test(value)) return false;
  const raw = value.replace(/\/\*[\s\S]*?\*\//g, "").toLowerCase();
  const v = raw.replace(/[\u0000-\u0020\u007f-\u009f]/g, "");
  if (/[\\<]|javascript:|vbscript:|data:|expression\(|-moz-binding|@import|position:(fixed|sticky)/.test(v)) return false;
  // 関数の名前は空白を詰める前の字で読む (font:700 13px var(…) の var を 13pxvar と読まない)。名前と ( の間に字は挟めない
  for (const m of raw.matchAll(/\(/g)) {
    const name = /([a-z-]*)$/.exec(raw.slice(0, m.index))?.[1] ?? "";
    if (!ALLOWED_FUNCTIONS.has(name)) return false;
    if (name === "url" && !/^\(\s*['"]?#/.test(raw.slice(m.index))) return false;
  }
  return true;
}

// 値が読まれても何も取りに行かない属性 (説明の文字と印)。図の説明に「data: 」や括弧があっても落とさない
const INERT_PROPERTY = /^(data|aria|role$|classname$|id$)/;
// 図の中で使ってよい id。生成元が付ける接頭辞だけ (ページの id と重ねて DOM を書き換えられないように)
const FIGURE_ID = /^(lesson-diagram|ld)-[A-Za-z0-9-]+$/;
// 図の塊の <div> の印。zukai の図は id を持たず、版つきの class を持つ
const FIGURE_ROOT_ID = /^lesson-diagram-[A-Za-z0-9-]+$/;
const FIGURE_ROOT_CLASS = /^zukai-[a-z0-9-]+-v[0-9]+$/;

function propertyValueText(value: unknown): string {
  if (Array.isArray(value)) return value.join(" ");
  return value == null ? "" : String(value);
}

function sanitizeProperties(element: Element): void {
  const kept: Element["properties"] = {};
  for (const [property, value] of Object.entries(element.properties ?? {})) {
    // data 属性は hast では dataFoo の形 (datasrc のような別の属性と分ける)
    const isData = /^data[A-Z0-9]/.test(property);
    const name = property.replace(/[-:]/g, "").toLowerCase();
    if (!ALLOWED_PROPERTIES.has(name) && !isData) continue;
    if (name.startsWith("on")) continue;
    if (name === "id" && !FIGURE_ID.test(propertyValueText(value))) continue;
    if (name === "classname") {
      // class は図の中の印として読むだけ。ページの class (prose、flex など) と重ねても、図の <style> の印にはならない
      kept[property] = value;
      continue;
    }
    if (!INERT_PROPERTY.test(name) && !isData && !isSafeCssValue(propertyValueText(value))) continue;
    kept[property] = name === "d" && element.tagName === "path" ? repairPathData(propertyValueText(value)) : value;
  }
  element.properties = kept;
}

/**
 * 図の塊の印。<style> のセレクタは、どれもこのどれかで始まらなければならない。
 * 塊の <div> の id (lesson-diagram-…) か、zukai の版つきの class。幅で出し分ける図は、塊の id で始まる中の <div> の id も使う
 */
function scopesOf(root: Element): string[] {
  const scopes: string[] = [];
  const rootId = root.properties?.id;
  const classes = root.properties?.className;
  for (const c of Array.isArray(classes) ? classes : []) {
    if (typeof c === "string" && FIGURE_ROOT_CLASS.test(c)) scopes.push(`.${c}`);
  }
  if (typeof rootId !== "string" || !FIGURE_ROOT_ID.test(rootId)) return scopes;
  scopes.push(`#${rootId}`);
  const walk = (el: Element): void => {
    for (const child of el.children) {
      if (child.type !== "element") continue;
      const id = child.properties?.id;
      if (child.tagName === "div" && typeof id === "string" && id.startsWith(`${rootId}-`) && FIGURE_ID.test(id)) scopes.push(`#${id}`);
      walk(child);
    }
  };
  walk(root);
  return scopes;
}

/**
 * CSS を規則の並びに分ける。文字列とコメントを飛ばして { } を数える (ブラウザと同じ切れ目で切る)。
 * 読めない形なら null
 */
function splitRules(css: string): Array<{ prelude: string; body: string }> | null {
  const rules: Array<{ prelude: string; body: string }> = [];
  let i = 0;
  let prelude = "";
  while (i < css.length) {
    const ch = css[i];
    if (ch === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2);
      if (end === -1) return null;
      i = end + 2;
      continue;
    }
    if (ch === "}") return null;
    if (ch === "{") {
      let depth = 1;
      let j = i + 1;
      let body = "";
      while (j < css.length && depth > 0) {
        const c = css[j];
        if (c === "/" && css[j + 1] === "*") {
          const end = css.indexOf("*/", j + 2);
          if (end === -1) return null;
          j = end + 2;
          continue;
        }
        if (c === '"' || c === "'") {
          const end = css.indexOf(c, j + 1);
          if (end === -1 || css.slice(j, end).includes("\n")) return null;
          body += css.slice(j, end + 1);
          j = end + 1;
          continue;
        }
        if (c === "{") depth++;
        if (c === "}") depth--;
        if (depth > 0) body += c;
        j++;
      }
      if (depth !== 0) return null;
      rules.push({ prelude: prelude.trim(), body });
      prelude = "";
      i = j;
      continue;
    }
    if (ch === '"' || ch === "'") {
      // セレクタの中の文字列 ([data-theme="dark"] など) は飛ばして読む。中の { } で切れ目をずらさない
      const end = css.indexOf(ch, i + 1);
      if (end === -1 || css.slice(i, end).includes("\n")) return null;
      prelude += css.slice(i, end + 1);
      i = end + 1;
      continue;
    }
    prelude += ch;
    i++;
  }
  return prelude.trim() === "" ? rules : null;
}

function selectorIsScoped(selector: string, scopes: string[]): boolean {
  const s = selector.trim();
  // 兄弟の結合子 (~ と、括弧の外の +) は、印の要素の外 (本文のほかの段落) を選べるので許さない
  if (s.includes("~") || /\+/.test(s.replace(/\([^()]*\)/g, ""))) return false;
  return scopes.some((scope) => s.startsWith(scope) && !/[\w-]/.test(s.charAt(scope.length)));
}

/**
 * 図の印で始まり、入れ子の無い規則だけを残した CSS。読めない形なら null。
 * 印の外を選ぶ規則 (html[data-theme="dark"] .zukai-… のような、ページの側から書いた規則) は、その規則だけを落とす
 */
function scopedRules(css: string, scopes: string[], dropped: { n: number }, depth = 0): string | null {
  if (depth > 3) return null;
  const rules = splitRules(css);
  if (!rules) return null;
  const kept: string[] = [];
  for (const { prelude, body } of rules) {
    if (prelude.startsWith("@")) {
      const inner = /^@media\b/i.test(prelude) ? scopedRules(body, scopes, dropped, depth + 1) : null;
      if (inner) kept.push(`${prelude}{${inner}}`);
      else dropped.n++;
      continue;
    }
    // 宣言の中の入れ子 (#f { & ~ p {…} } や @scope) は、外側の印を外れて図の外を選べるので許さない
    const ok = prelude !== "" && !/[{}&@]/.test(body)
      && prelude.split(",").every((selector) => selectorIsScoped(selector, scopes)) && isSafeCssValue(body);
    if (ok) kept.push(`${prelude}{${body}}`);
    else dropped.n++;
  }
  return kept.join("\n");
}

/**
 * 図の <style> のうち、残してよい規則だけの CSS。残せる規則が 1 つも無ければ null。
 * 残すのは、外への参照が無く、セレクタが全部図の塊の印で始まり、入れ子の無い規則 (と、その規則だけを持つ @media)。
 * 印の外を選ぶ規則 (html[data-theme="dark"] .zukai-… のような、ページの側から書いた規則) は、その規則だけを落とす
 */
export function scopedFigureCss(css: string, scopes: string[], dropped: { n: number } = { n: 0 }): string | null {
  if (scopes.length === 0) return null;
  if (/[\\<]/.test(css)) return null;
  if (/@(import|font-face|namespace|charset)/i.test(css)) return null;
  const kept = scopedRules(css, scopes, dropped);
  return kept ? kept : null;
}

/** 図の <style> を、規則を 1 つも落とさずに残せるか */
export function isScopedFigureCss(css: string, scopes: string[]): boolean {
  const dropped = { n: 0 };
  return scopedFigureCss(css, scopes, dropped) !== null && dropped.n === 0;
}

function textOf(element: Element): string {
  return element.children.map((child) => (child.type === "text" ? child.value : "")).join("");
}

// 図の <svg> の外 (塊の <div> の中) で許す要素。title や text のような SVG の要素は <svg> の中だけ
// (svg の外の <title> は React がページの題名として head へ移す)
const OUTSIDE_SVG = new Set(["div", "style", "svg"]);

function sanitizeChildren(children: Node[], scopes: string[], inSvg: boolean): ElementContent[] {
  const safe: ElementContent[] = [];
  for (const node of children) {
    if (node.type === "text") {
      safe.push(node);
      continue;
    }
    if (node.type !== "element") continue; // コメント、doctype、解けない raw は落とす
    const name = node.tagName.toLowerCase();
    if (!ALLOWED_ELEMENTS.has(name)) continue; // 中身ごと落とす
    if (inSvg ? name === "div" || name === "style" : !OUTSIDE_SVG.has(name)) continue;
    if (name === "style") {
      const css = scopedFigureCss(textOf(node), scopes);
      if (css === null) continue;
      node.children = [{ type: "text", value: css }];
      node.properties = {};
      safe.push(node);
      continue;
    }
    sanitizeProperties(node);
    node.children = sanitizeChildren(node.children as Node[], scopes, inSvg || name === "svg");
    safe.push(node);
  }
  return safe;
}

/**
 * 図の塊の HTML 1 つを、許した要素だけの木にする。図として読めなければ null。
 * 返す木は、描画の範囲を図の箱に閉じる包み (contain: paint) に入れる。図の CSS が position や z-index を使っても、
 * 本文や画面の上には出ない
 */
export function sanitizeSvgFigure(html: string): Element | null {
  if (!FIGURE_START.test(html.trim())) return null;
  const parsed = rehypeRaw()({ type: "root", children: [{ type: "raw", value: html } as never] }, undefined as never);
  const roots = parsed.children.filter((child): child is Element => child.type === "element");
  const stray = parsed.children.some((child) => child.type === "text" && child.value.trim() !== "");
  if (roots.length !== 1 || roots[0].tagName !== "div" || stray) return null;
  const root = roots[0];
  // 開きタグの文字列ではなく、読んだ属性で印を見る (title="data-lesson-diagram=…" のような値の中の字では図にしない)
  if (!("dataLessonDiagram" in (root.properties ?? {})) && !("dataZukaiFigure" in (root.properties ?? {}))) return null;
  const scopes = scopesOf(root);
  const [figure] = sanitizeChildren([root], scopes, false);
  if (!figure || figure.type !== "element") return null;
  const hasSvg = (el: Element): boolean =>
    el.tagName === "svg" || el.children.some((c) => c.type === "element" && hasSvg(c));
  if (!hasSvg(figure)) return null;
  return {
    type: "element",
    tagName: "div",
    properties: { className: ["svg-figure"], style: "contain:paint;position:relative" },
    children: [figure],
  };
}

function visit(parent: Root | Element): void {
  const next: Array<RootContent | ElementContent> = [];
  for (const child of parent.children as Node[]) {
    if (child.type === "raw" && FIGURE_START.test(child.value.trim())) {
      // 図として読めない塊は、そのまま raw に残す (react-markdown が文字として出す。黙って消さない)
      next.push((sanitizeSvgFigure(child.value) ?? child) as ElementContent);
      continue;
    }
    if (child.type === "element") visit(child);
    next.push(child as ElementContent);
  }
  parent.children = next as never;
}

/** 図の塊の raw だけを要素にする。ほかの raw は触らない (react-markdown が文字として出す) */
export function rehypeSvgFigures() {
  return (tree: Root): void => {
    visit(tree);
  };
}
